import { gql } from "@apollo/client";
import React, { useState } from "react";
import { Image } from "react-bootstrap";
import styled from "styled-components";

import { PrimaryButton } from "~/components/common/buttons";
import { Toolbar } from "~/components/common/toolbars";
import Dialog from "~/components/generic/Dialog";
import LoadingSpinner from "~/components/generic/LoadingSpinner";
import {
  useCreateSubpageMediaMutation,
  useSubpageMediaQuery,
} from "~/components/Overview/SubpageMediaModal.generated";

export const SUBPAGE_MEDIA_QUERY = gql`
  query SubpageMedia($subpageId: ID!, $subjectType: String!) {
    subpageMedia(subpageId: $subpageId, subjectType: $subjectType) {
      id
      url
    }
  }
`;

export const CREATE_SUBPAGE_MEDIA_MUTATION = gql`
  mutation CreateSubpageMedia(
    $subpageId: ID!
    $subjectId: ID!
    $subjectType: String!
    $file: Upload!
  ) {
    createSubpageMedia(
      subpageId: $subpageId
      subjectId: $subjectId
      subjectType: $subjectType
      image: $file
    ) {
      subpageMedia {
        ... on SubpageMedia {
          id
          url
        }
        ... on HoldCoSubpageMedia {
          id
          url
        }
      }
    }
  }
`;

interface SubpageMediaModalProps {
  show: boolean;
  onHide: () => void;
  onInsert: (id: string) => void;
  subpageId: string;
  subjectId: string;
  subjectType: string;
}

const SubpageMediaModal = ({
  show,
  onHide,
  onInsert,
  subpageId,
  subjectId,
  subjectType,
}: SubpageMediaModalProps) => {
  const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);
  const [createSubpageMedia, { loading: subpageMediaLoading }] =
    useCreateSubpageMediaMutation();
  const { data, loading, error } = useSubpageMediaQuery({
    variables: { subpageId, subjectType },
  });

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      const file = event.target.files[0];
      if (file) {
        try {
          const response = await createSubpageMedia({
            variables: {
              subpageId: subpageId,
              subjectId: subjectId,
              subjectType: subjectType,
              file: file,
            },
            refetchQueries: ["SubpageMedia"],
            awaitRefetchQueries: true,
          });
          const newMediaId =
            response.data?.createSubpageMedia?.subpageMedia?.id;
          if (newMediaId) {
            setSelectedMediaId(newMediaId);
          }
        } catch (error) {
          console.error("Error uploading file:", error);
        }
      }
    }
  };

  const handleImageClick = (id: string) => {
    setSelectedMediaId(id);
  };

  return (
    <Dialog
      isOpen={show}
      onRequestClose={onHide}
      header="Import or Select Media"
      widthVariant={"medium"}
      actions={
        <Toolbar>
          <PrimaryButton
            onClick={() => selectedMediaId && onInsert(selectedMediaId)}
            disabled={!selectedMediaId}
          >
            Insert
          </PrimaryButton>
        </Toolbar>
      }
    >
      {subpageMediaLoading && (
        <SpinnerOverlay>
          <LoadingSpinner color="dark" />
        </SpinnerOverlay>
      )}
      <div>
        <b>
          Import image from computer:{" "}
          <input type="file" onChange={handleFileUpload} />
        </b>
      </div>
      <div className="mt-4">
        <b>Select from imported media:</b>
        <StyledMediaGallery>
          {data?.subpageMedia?.map((media) => (
            <StyledImage
              key={media.id}
              src={media.url}
              thumbnail
              isSelected={media.id === selectedMediaId}
              onClick={() => handleImageClick(media.id)}
            />
          ))}
        </StyledMediaGallery>
        {loading && <p>Loading...</p>}
        {error && <p>Error loading media</p>}
      </div>
    </Dialog>
  );
};

export default SubpageMediaModal;

const SpinnerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const StyledImage = styled(({ isSelected, ...rest }) => <Image {...rest} />)<{
  isSelected: boolean;
}>`
  border: ${({ isSelected }) => (isSelected ? "3px solid blue" : "none")};
  cursor: pointer;
  margin: 5px;
`;

const StyledMediaGallery = styled.div`
  max-height: 60vh; /* Limits the height to 60% of the viewport height */
  overflow-y: auto; /* Enables vertical scrolling */
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;
